import {
    SessionQuestionEntityFrontend,
    QuestionOptionDtoFrontend,
    AnswerTypeFrontend,
    WorkerDtoFrontend,
    getAnswerForQuestion,
} from '../api/sessionQuestionsApi';

export interface CategorizedQuestion {
    id: number;
    label: string;
    formattedAnswer: string;
    rawAnswer: any;
    enabled: boolean;
}

export interface CategorizedData {
    personalInfo: CategorizedQuestion[];
    professionalInfo: CategorizedQuestion[];
    otherInfo: CategorizedQuestion[];
    professionContext: CategorizedQuestion[];
}

function optionLabel(options: QuestionOptionDtoFrontend[] | undefined, value: any): string {
    if (!options) return String(value);
    for (const opt of options) {
        if (opt.key === value) {
            return opt.label || String(value);
        }
    }
    return String(value);
}

function formatAnswer(q: SessionQuestionEntityFrontend, answer: any): string {
    if (answer === null || answer === undefined) return '';
    if (answer === '' || (Array.isArray(answer) && answer.length === 0)) return 'None';
    if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';

    const options = q.options || [];
    const answerType = (q.answerType || '').toUpperCase();

    if (answerType === AnswerTypeFrontend.RADIO && typeof answer === 'string' && options.length > 0) {
        return optionLabel(options, answer);
    }

    if (answerType === AnswerTypeFrontend.MULTISELECT && Array.isArray(answer)) {
        if (answer.length === 0) return 'None';
        if (options.length > 0) {
            return answer.map((v) => optionLabel(options, v)).join(', ');
        }
        return answer.map(String).join(', ');
    }

    if (answerType === AnswerTypeFrontend.LIST_OBJECT && Array.isArray(answer)) {
        if (answer.length === 0) return 'None';
        const schema = q.listItemSchema || [];
        const maxItems = 2;
        const maxFields = 4;
        const parts: string[] = [];

        for (const item of answer.slice(0, maxItems)) {
            if (typeof item !== 'object' || item === null) {
                parts.push(String(item));
                continue;
            }
            const itemParts: string[] = [];
            if (schema.length > 0) {
                for (const fd of schema.slice(0, maxFields)) {
                    const key = fd.fieldName;
                    if (!key) continue;
                    const raw = item[key];
                    if (raw === null || raw === undefined || raw === '') continue;
                    const fdOpts = fd.options || [];
                    let rawStr: string;
                    if (Array.isArray(raw)) {
                        rawStr = fdOpts.length > 0
                            ? raw.map((v: any) => optionLabel(fdOpts, v)).join(', ')
                            : raw.map(String).join(', ');
                    } else {
                        rawStr = fdOpts.length > 0 ? optionLabel(fdOpts, raw) : String(raw);
                    }
                    itemParts.push(`${fd.label || key}: ${rawStr}`);
                }
            } else {
                for (const k of Object.keys(item).slice(0, maxFields)) {
                    const v = item[k];
                    if (v === null || v === undefined || v === '') continue;
                    itemParts.push(`${k}: ${v}`);
                }
            }
            parts.push(itemParts.length > 0 ? itemParts.join(', ') : String(item));
        }

        const suffix = answer.length > maxItems ? ` (+${answer.length - maxItems} more)` : '';
        return parts.join(' | ') + suffix;
    }

    if (Array.isArray(answer)) return answer.map(String).join(', ');

    if (typeof answer === 'object') {
        // Generic-object fallback — non-array, non-list-object payload. The
        // dedicated helper was removed in the 75dc942 refactor but this
        // call site was missed. Same key:value join used inside the
        // LIST_OBJECT branch above when there's no schema, so we apply it
        // here too rather than reintroducing a single-use helper.
        const parts = Object.entries(answer)
            .filter(([, v]) => v !== null && v !== undefined && v !== '')
            .map(([k, v]) => `${k}: ${v}`);
        return parts.join(', ');
    }

    return String(answer);
}

export function categorizeSessionQuestions(
    questions: SessionQuestionEntityFrontend[],
    worker: WorkerDtoFrontend | null,
): CategorizedData {
    const personalInfo: CategorizedQuestion[] = [];
    const professionalInfo: CategorizedQuestion[] = [];
    const otherInfo: CategorizedQuestion[] = [];
    const professionContext: CategorizedQuestion[] = [];

    for (const q of questions) {
        // Project the answer from the worker entity (the source of truth post-PR1)
        // rather than reading a stale `q.answer` field that no longer exists.
        const rawAnswer = getAnswerForQuestion(q, worker);
        if (rawAnswer === null || rawAnswer === undefined) continue;

        const label = q.questionShorten || q.question || q.questionKey || 'Question';
        const formattedAnswer = formatAnswer(q, rawAnswer);

        const item: CategorizedQuestion = {
            id: q.id,
            label,
            formattedAnswer,
            rawAnswer,
            enabled: true,
        };

        const qtype = (q.questionType || '').toUpperCase();
        const qkey = (q.questionKey || '').toUpperCase();

        // Questions with a professionKey (from profession-specific Q&A) go to professionContext
        if ((q as any).professionKey) {
            professionContext.push(item);
        } else if (qtype === 'PERSONAL' || qkey.startsWith('PERSONAL_')) {
            personalInfo.push(item);
        } else if (qtype === 'PROFESSION' || qkey.startsWith('PROFESSION_')) {
            professionalInfo.push(item);
        } else {
            otherInfo.push(item);
        }
    }

    return { personalInfo, professionalInfo, otherInfo, professionContext };
}

export function buildPromptPreview(
    data: CategorizedData,
    categoryToggles: { personal: boolean; professional: boolean; other: boolean; professionContext: boolean },
    extraInstructions: string,
    language: string = 'NL',
): string {
    let prompt = `You are a professional CV writer. Create a concise, professional paragraph summary of a person based on their information.\n\n`;
    prompt += `Language: ${language}\n`;
    prompt += `Tone: Professional, confident, and engaging\n`;
    prompt += `Length: 3-4 sentences maximum\n`;
    prompt += `Focus: Professional skills, experience, and key strengths\n`;

    if (categoryToggles.personal && data.personalInfo.length > 0) {
        prompt += `\nPersonal Information:\n`;
        for (const q of data.personalInfo) {
            if (q.enabled) prompt += `- ${q.label}: ${q.formattedAnswer}\n`;
        }
    }

    if (categoryToggles.professional && data.professionalInfo.length > 0) {
        prompt += `\nProfessional Information:\n`;
        for (const q of data.professionalInfo) {
            if (q.enabled) prompt += `- ${q.label}: ${q.formattedAnswer}\n`;
        }
    }

    if (categoryToggles.other && data.otherInfo.length > 0) {
        prompt += `\nOther Information:\n`;
        for (const q of data.otherInfo) {
            if (q.enabled) prompt += `- ${q.label}: ${q.formattedAnswer}\n`;
        }
    }

    if (categoryToggles.professionContext && data.professionContext.length > 0) {
        prompt += `\nProfession-specific answered questions (display values):\n`;
        for (const q of data.professionContext) {
            if (q.enabled) prompt += `- ${q.label}: ${q.formattedAnswer}\n`;
        }
    }

    if (extraInstructions.trim()) {
        prompt += `\nAdditional instructions from recruiter:\n${extraInstructions.trim()}\n`;
    }

    prompt += `\nBased on this information, write a professional paragraph in ${language} that summarizes this person's professional profile.\n`;
    prompt += `Focus on their key skills, experience, and what makes them valuable as a professional.\n`;
    prompt += `Keep it concise, professional, and engaging.\n`;

    return prompt;
}
